/**
 * Раздел «Статьи» (экраны 11–12 ТЗ): категории и материалы из списка
 * «Примеры материалов».
 *
 * Статьи — статический контент в репозитории, а не таблица в БД: редакция
 * правит их пулл-реквестом вместе с остальным копирайтингом, а CMS с
 * модерацией в ТЗ нет. Переезд в БД — замена этого файла на запрос к API,
 * экраны при этом не меняются.
 */
export type ArticleCategory = "about" | "weight" | "nutrition" | "lifestyle" | "news" | "faq";

export const CATEGORY_LABELS: Record<ArticleCategory, string> = {
  about: "О тирзепатиде",
  weight: "Снижение веса",
  nutrition: "Питание",
  lifestyle: "Образ жизни",
  news: "Новости",
  faq: "FAQ",
};

export const CATEGORIES = Object.keys(CATEGORY_LABELS) as ArticleCategory[];

/** Блоки статьи — те, что разрешает ТЗ: текст, факт, схема (шаги), список, FAQ. */
export type ArticleBlock =
  | { type: "p"; text: string }
  | { type: "h"; text: string }
  | { type: "fact"; text: string }
  | { type: "list"; items: string[] }
  | { type: "steps"; items: Array<{ title: string; body: string }> }
  | { type: "faq"; items: Array<{ q: string; a: string }> };

export type Article = {
  slug: string;
  category: ArticleCategory;
  title: string;
  excerpt: string;
  readingMin: number;
  /** ISO-дата публикации. */
  date: string;
  blocks: ArticleBlock[];
};

export const articles: Article[] = [
  {
    slug: "how-tirzepatide-works",
    category: "about",
    title: "Как работает тирзепатид",
    excerpt: "Механизмы аппетита и насыщения, на которые воздействует препарат, — простыми словами.",
    readingMin: 5,
    date: "2026-09-15",
    blocks: [
      { type: "p", text: "Тирзепатид воздействует на механизмы, связанные с аппетитом и чувством насыщения. Благодаря этому становится проще контролировать питание и постепенно снижать вес." },
      { type: "fact", text: "Препарат работает не «вместо» питания и активности, а вместе с ними: он помогает удерживать выбранный режим." },
      { type: "steps", items: [
        { title: "Меньше чувства голода", body: "Аппетит становится легче контролировать." },
        { title: "Насыщение наступает раньше", body: "Проще контролировать размер порций." },
        { title: "Меньше тяги к еде", body: "Еда занимает меньше внимания в течение дня." },
      ] },
      { type: "p", text: "Изменения формируются постепенно. Результаты индивидуальны и могут отличаться — схему применения подбирает специалист." },
    ],
  },
  {
    slug: "what-is-food-noise",
    category: "about",
    title: "Что такое food noise и почему он возникает",
    excerpt: "Постоянные мысли о еде — не слабость характера, а сигнал, с которым можно работать.",
    readingMin: 4,
    date: "2026-09-12",
    blocks: [
      { type: "p", text: "Food noise — это навязчивые мысли о еде: что съесть, когда и сколько, даже если голода нет. Они отвлекают в течение дня и мешают придерживаться режима." },
      { type: "p", text: "Такие мысли усиливаются на фоне нерегулярного питания, недосыпа и стресса: организм подталкивает к быстрым источникам энергии." },
      { type: "fact", text: "Меньше постоянных мыслей о еде и желания перекусить — одно из изменений, которое отмечают при применении тирзепатида." },
      { type: "list", items: ["Регулярные приёмы пищи снижают «шум» сами по себе.", "Белок и клетчатка в каждом приёме продлевают насыщение.", "Сон и вода — недооценённые инструменты контроля аппетита."] },
    ],
  },
  {
    slug: "why-weight-changes-unevenly",
    category: "weight",
    title: "Почему вес может меняться неравномерно",
    excerpt: "Плато и колебания — нормальная часть процесса. Что стоит за цифрами на весах.",
    readingMin: 4,
    date: "2026-09-10",
    blocks: [
      { type: "p", text: "Вес — это не только жировая ткань. Вода, содержимое кишечника, гликоген в мышцах и цикл у женщин меняют цифру на весах на 1–2 кг в течение недели." },
      { type: "fact", text: "Оценивайте динамику по средним значениям за неделю и по объёмам, а не по одному взвешиванию." },
      { type: "p", text: "Плато — период, когда организм адаптируется к новому весу. Это не остановка результата: при соблюдении рекомендаций снижение может продолжаться постепенно." },
    ],
  },
  {
    slug: "nutrition-and-results",
    category: "nutrition",
    title: "Как питание влияет на результат",
    excerpt: "Что есть, когда аппетит снизился: как не потерять белок и энергию.",
    readingMin: 5,
    date: "2026-09-08",
    blocks: [
      { type: "p", text: "Когда чувство голода снижается, легко пропускать приёмы пищи. Но дефицит белка и микроэлементов замедляет результат и ухудшает самочувствие." },
      { type: "list", items: ["Белок в каждом приёме пищи: рыба, птица, яйца, творог, бобовые.", "Овощи и клетчатка — для насыщения и пищеварения.", "Небольшие порции чаще, если большие не заходят.", "Достаточно воды — 30 мл на килограмм веса как ориентир."] },
      { type: "fact", text: "Цель — не есть меньше любой ценой, а есть достаточно нужного." },
    ],
  },
  {
    slug: "before-you-start",
    category: "lifestyle",
    title: "Что важно знать перед началом применения",
    excerpt: "Консультация, схема, хранение и первые недели — короткий чек-лист.",
    readingMin: 6,
    date: "2026-09-05",
    blocks: [
      { type: "steps", items: [
        { title: "Консультация", body: "Обсудите со специалистом цели, противопоказания и схему применения." },
        { title: "Схема", body: "Препарат применяется по установленной схеме; дозировку меняют только по рекомендации." },
        { title: "Хранение", body: "В холодильнике при 2–8 °C, не замораживать, беречь от света." },
        { title: "Первые недели", body: "Организм адаптируется: возможен дискомфорт со стороны пищеварения, чаще всего он проходит." },
      ] },
      { type: "p", text: "При появлении нежелательных реакций обратитесь к специалисту. Информация в приложении не заменяет консультацию." },
    ],
  },
  {
    slug: "faq-tirzepatide",
    category: "faq",
    title: "Частые вопросы о тирзепатиде",
    excerpt: "Эффект, применение, побочные эффекты, хранение и консультация — коротко.",
    readingMin: 3,
    date: "2026-09-03",
    blocks: [
      { type: "faq", items: [
        { q: "Как быстро появляется эффект?", a: "Изменения происходят постепенно; результаты индивидуальны и могут отличаться." },
        { q: "Как применяется тирзепатид?", a: "По установленной схеме в соответствии с рекомендациями специалиста." },
        { q: "Есть ли побочные эффекты?", a: "Возможны, чаще со стороны пищеварения в начале применения. При появлении реакций обратитесь к специалисту." },
        { q: "Как хранить препарат?", a: "В холодильнике при 2–8 °C, не замораживая и защищая от света." },
        { q: "Можно ли получить консультацию перед покупкой?", a: "Да, бесплатно — через кнопку «Получить консультацию»." },
      ] },
    ],
  },
  {
    slug: "news-and-research",
    category: "news",
    title: "Новости и исследования",
    excerpt: "Что публикуют исследователи о препаратах для контроля веса — без сенсаций.",
    readingMin: 4,
    date: "2026-09-01",
    blocks: [
      { type: "p", text: "Интерес к препаратам, влияющим на аппетит, растёт: публикации изучают долгосрочные эффекты, влияние на пищевое поведение и качество жизни." },
      { type: "fact", text: "Мы будем публиковать здесь короткие пересказы новых материалов с ссылками на источники." },
      { type: "p", text: "Раздел обновляется. Если вы хотите получать новости первыми — напишите нам через кнопку «Получить консультацию»." },
    ],
  },
];

export const articleBySlug = (slug: string) => articles.find((article) => article.slug === slug);
